'use client';

import { MembershipPackageName } from 'common/interfaces/api';
import { ScreenName } from 'common/utils/pp_tracking';
import Image from 'next/image';
import useGlobalStore from 'store/global';

const CheckIcon = () => (
  <span className="inline-block size-5 rounded-full bg-pp-green-primary" />
);

const MembershipSection = () => {
  const setShowMembershipPayDialog = useGlobalStore(
    (state) => state.setShowMembershipPayDialog,
  );
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const handleOpenMembershipDialog = (packageName: MembershipPackageName) => {
    if (!currentAuth) {
      showLogin({});
      return;
    }
    setShowMembershipPayDialog({
      open: true,
      showMembershipStatus: false,
      initPackage: packageName,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      screen: ScreenName.AboutPage,
    });
  };
  return (
    <section
      id="membership-section"
      className="w-full bg-white px-6 py-20"
      style={{ scrollMarginTop: '110px' }}
    >
      <div className="mx-auto max-w-7xl">
        <h2 className="mb-4 text-center text-3xl font-bold text-pp-text-primary md:text-4xl">
          メンバーシップ
        </h2>
        <p className="mb-10 text-center text-base text-pp-text-secondary md:text-lg">
          クリエイターやPostPrime専門チャンネルのプライム登録でより深い情報にアクセス
        </p>
        {/* Chart Images Placeholder */}
        <div className="mx-auto mb-10 max-w-6xl">
          <h3 className="mb-6 text-center text-xl font-bold text-pp-text-primary md:text-2xl">
            チャート表示イメージ
          </h3>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <div className="relative h-[180px] overflow-hidden rounded-2xl shadow-md md:h-[220px]">
                <Image
                  src="/images/about/about-membership-1.jpg"
                  alt="中央値チャート"
                  fill
                  className="object-cover"
                />
              </div>
              <h4 className="mt-3 text-center text-lg font-bold text-pp-text-primary">
                中央値チャート
              </h4>
            </div>
            <div>
              <div className="relative h-[180px] overflow-hidden rounded-2xl shadow-md md:h-[220px]">
                <Image
                  src="/images/about/about-membership-2.jpg"
                  alt="投票率チャート"
                  fill
                  className="object-cover"
                />
              </div>
              <h4 className="mt-3 text-center text-lg font-bold text-pp-text-primary">
                投票率チャート
              </h4>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl">
          {/* Pricing Table */}
          <div className="overflow-x-auto rounded-2xl shadow-lg ring-1 ring-gray-200">
            <table
              className="w-full border-separate"
              style={{ borderSpacing: '2px' }}
            >
              <thead>
                <tr>
                  <th
                    className="p-4 text-left text-sm font-bold text-white md:text-base"
                    style={{
                      background: 'linear-gradient(135deg, #00c853, #00a143)',
                    }}
                  >
                    &nbsp;
                  </th>
                  <th
                    className="p-4 text-center text-sm font-bold text-white md:text-2xl"
                    style={{
                      background: 'linear-gradient(135deg, #00c853, #00a143)',
                    }}
                  >
                    Platinum
                  </th>
                  <th
                    className="p-4 text-center text-sm font-bold text-white md:text-2xl"
                    style={{
                      background: 'linear-gradient(135deg, #00c853, #00a143)',
                    }}
                  >
                    Gold
                  </th>
                  <th
                    className="p-4 text-center text-sm font-bold text-white md:text-2xl"
                    style={{
                      background: 'linear-gradient(135deg, #00c853, #00a143)',
                    }}
                  >
                    Silver
                  </th>
                  <th
                    className="p-4 text-center text-sm font-bold text-white md:text-2xl"
                    style={{
                      background: 'linear-gradient(135deg, #00c853, #00a143)',
                    }}
                  >
                    Green
                  </th>
                </tr>
              </thead>
              <tbody className="[&_td]:p-3 [&_td]:text-center md:[&_td]:p-5 [&_th]:p-5 [&_th]:text-left [&_th]:text-sm [&_th]:text-pp-text-primary [&_th]:md:text-base">
                <tr className="[&_td]:bg-pp-green-subtle [&_th]:bg-pp-green-subtle">
                  <th className="text-sm font-semibold md:text-base">
                    投票率チャート：
                    <br />
                    対象期間
                  </th>
                  <td>全期間</td>
                  <td>全期間</td>
                  <td>全期間</td>
                  <td>1日</td>
                </tr>

                <tr className="[&_td]:bg-white [&_th]:bg-white">
                  <th className="text-sm font-semibold md:text-base">
                    投票率チャート：
                    <br />
                    中央値の表示と通知
                  </th>
                  <td>
                    <CheckIcon />
                  </td>
                  <td>
                    <CheckIcon />
                  </td>
                  <td></td>
                  <td></td>
                </tr>

                <tr className="[&_td]:bg-pp-green-subtle [&_th]:bg-pp-green-subtle">
                  <th className="text-sm font-semibold md:text-base">
                    投票率チャート：
                    <br />
                    コンビネーション表示
                  </th>
                  <td>
                    <CheckIcon />
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>

                <tr className="[&_td]:bg-white [&_th]:bg-white">
                  <th className="text-sm font-semibold md:text-base">
                    コンテンツ割引
                  </th>
                  <td className="font-semibold">23%OFF</td>
                  <td className="font-semibold">20%OFF</td>
                  <td className="font-semibold">15%OFF</td>
                  <td className="font-semibold">10%OFF</td>
                </tr>

                <tr className="[&_td]:bg-pp-green-subtle [&_th]:bg-pp-green-subtle">
                  <th className="text-sm font-semibold md:text-base">
                    特別イベントに招待
                  </th>
                  <td>
                    <CheckIcon />
                  </td>
                  <td>
                    <CheckIcon />
                  </td>
                  <td></td>
                  <td></td>
                </tr>

                <tr
                  style={{
                    background:
                      'linear-gradient(135deg, var(--pp-green-subtle), var(--pp-bg-tertiary))',
                  }}
                >
                  <th className="text-sm font-bold md:text-base">料金</th>
                  <td>
                    <span className="text-sm font-extrabold text-pp-green-primary md:text-2xl">
                      5,850円/月
                    </span>
                  </td>
                  <td>
                    <span className="text-sm font-extrabold text-pp-green-primary md:text-2xl">
                      3,600円/月
                    </span>
                  </td>
                  <td>
                    <span className="text-sm font-extrabold text-pp-green-primary md:text-2xl">
                      1,650円/月
                    </span>
                  </td>
                  <td>
                    <span className="text-sm font-extrabold text-pp-green-primary md:text-2xl">
                      920円/月
                    </span>
                  </td>
                </tr>

                <tr className="[&_td]:bg-white [&_th]:bg-white">
                  <th></th>
                  <td>
                    <button
                      onClick={() =>
                        handleOpenMembershipDialog(
                          MembershipPackageName.Platinum,
                        )
                      }
                      className="hover:bg-pp-green-secondary w-full rounded-full bg-pp-green-primary px-4 py-3 text-sm font-bold text-white transition-all hover:scale-105 md:px-6 md:text-base"
                    >
                      <span className="hidden md:inline">Platinumに</span>
                      登録
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() =>
                        handleOpenMembershipDialog(MembershipPackageName.Gold)
                      }
                      className="hover:bg-pp-green-secondary w-full rounded-full bg-pp-green-primary px-4 py-3 text-sm font-bold text-white transition-all hover:scale-105 md:px-6 md:text-base"
                    >
                      <span className="hidden md:inline">Goldに</span>
                      登録
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() =>
                        handleOpenMembershipDialog(MembershipPackageName.Silver)
                      }
                      className="hover:bg-pp-green-secondary w-full rounded-full bg-pp-green-primary px-4 py-3 text-sm font-bold text-white transition-all hover:scale-105 md:px-6 md:text-base"
                    >
                      <span className="hidden md:inline">Silverに</span>
                      登録
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() =>
                        handleOpenMembershipDialog(MembershipPackageName.Green)
                      }
                      className="hover:bg-pp-green-secondary w-full rounded-full bg-pp-green-primary px-4 py-3 text-sm font-bold text-white transition-all hover:scale-105 md:px-6 md:text-base"
                    >
                      <span className="hidden md:inline">Greenに</span>
                      登録
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 space-y-1 text-sm text-pp-text-secondary">
            <p>※コンテンツ割引は、月額選択による割引です。</p>
            <p>
              ※料金は、Webによる決済金額です。アプリからの決済は、価格が異なる場合があります。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MembershipSection;
